/**
 * Permission utilities.
 *
 * Central place for all permission checks. Components and hooks call these
 * functions rather than inspecting the user object directly.
 *
 * NOTE: These are UI-layer checks only (show/hide, enable/disable).
 * Actual enforcement happens in Firestore Security Rules and Cloud Functions.
 */

import type { CmsUser, Audience, AudiencePermissions, UserRole } from '@/types';

// ─── Role check ────────────────────────────────────────────────────────────────

export function isAdmin(user: CmsUser | null): boolean {
  return user?.role === 'admin';
}

export function isApproved(user: CmsUser | null): boolean {
  return user?.status === 'approved';
}

export function isActiveUser(user: CmsUser | null): boolean {
  return isApproved(user) && user?.status !== 'deactivated';
}

// ─── Audience permission checks ────────────────────────────────────────────────

function getAudiencePerms(
  user: CmsUser | null,
  audience: Audience
): AudiencePermissions | null {
  if (!user || !isApproved(user)) return null;
  if (isAdmin(user)) {
    // Admins have all permissions implicitly
    return {
      view:           true,
      add:            true,
      edit:           true,
      delete:         true,
      uploadFiles:    true,
      removeFiles:    true,
      copyContent:    true,
      approveContent: true,
      publishContent: true,
    };
  }
  return user.permissions[audience] ?? null;
}

export function canView(user: CmsUser | null, audience: Audience): boolean {
  return getAudiencePerms(user, audience)?.view ?? false;
}

export function canAdd(user: CmsUser | null, audience: Audience): boolean {
  return getAudiencePerms(user, audience)?.add ?? false;
}

export function canEdit(user: CmsUser | null, audience: Audience): boolean {
  return getAudiencePerms(user, audience)?.edit ?? false;
}

export function canDelete(user: CmsUser | null, audience: Audience): boolean {
  return getAudiencePerms(user, audience)?.delete ?? false;
}

export function canUploadFiles(user: CmsUser | null, audience: Audience): boolean {
  return getAudiencePerms(user, audience)?.uploadFiles ?? false;
}

export function canRemoveFiles(user: CmsUser | null, audience: Audience): boolean {
  return getAudiencePerms(user, audience)?.removeFiles ?? false;
}

export function canCopyContent(user: CmsUser | null, audience: Audience): boolean {
  return getAudiencePerms(user, audience)?.copyContent ?? false;
}

export function canApproveContent(user: CmsUser | null, audience: Audience): boolean {
  return getAudiencePerms(user, audience)?.approveContent ?? false;
}

export function canPublishContent(user: CmsUser | null, audience: Audience): boolean {
  return getAudiencePerms(user, audience)?.publishContent ?? false;
}

/** Whether the user can write (add OR edit) to the given audience. */
export function canWrite(user: CmsUser | null, audience: Audience): boolean {
  return canAdd(user, audience) || canEdit(user, audience);
}

// ─── Global permission checks ──────────────────────────────────────────────────

export function canViewDashboard(user: CmsUser | null): boolean {
  if (!user || !isApproved(user)) return false;
  if (isAdmin(user)) return true;
  return user.permissions.global.viewDashboard;
}

export function canManageUsers(user: CmsUser | null): boolean {
  if (!user || !isApproved(user)) return false;
  return isAdmin(user);
}

export function canViewAuditLogs(user: CmsUser | null): boolean {
  if (!user || !isApproved(user)) return false;
  if (isAdmin(user)) return true;
  return user.permissions.global.viewAuditLogs;
}

// ─── Accessible audiences ──────────────────────────────────────────────────────

/**
 * Returns the list of audiences the user can view.
 * Used to filter which audience tabs to show.
 */
export function getAccessibleAudiences(user: CmsUser | null): Audience[] {
  const audiences: Audience[] = ['mothers', 'fathers', 'grandparents'];
  return audiences.filter(a => canView(user, a));
}

// ─── Admin default permissions ─────────────────────────────────────────────────

/**
 * Returns the full permission set to assign when a user is given the admin role.
 * Convenience used in the UI — actual enforcement is in Firestore rules.
 */
export function getAdminPermissions(): CmsUser['permissions'] {
  const fullAudience: AudiencePermissions = {
    view: true, add: true, edit: true, delete: true,
    uploadFiles: true, removeFiles: true, copyContent: true,
    approveContent: true, publishContent: true,
  };
  return {
    mothers:      fullAudience,
    fathers:      fullAudience,
    grandparents: fullAudience,
    global: {
      viewDashboard: true,
      manageUsers:   true,
      viewAuditLogs: true,
    },
  };
}

/**
 * Returns the permission set for a viewer role: view only on all audiences.
 */
export function getViewerPermissions(): CmsUser['permissions'] {
  const viewOnly: AudiencePermissions = {
    view: true, add: false, edit: false, delete: false,
    uploadFiles: false, removeFiles: false, copyContent: false,
    approveContent: false, publishContent: false,
  };
  return {
    mothers:      viewOnly,
    fathers:      viewOnly,
    grandparents: viewOnly,
    global: {
      viewDashboard: true,
      manageUsers:   false,
      viewAuditLogs: false,
    },
  };
}

/**
 * Returns suitable default permissions when assigning a role.
 */
export function getDefaultPermissionsForRole(role: UserRole): CmsUser['permissions'] {
  switch (role) {
    case 'admin':  return getAdminPermissions();
    case 'viewer': return getViewerPermissions();
    default:
      // editor — no default permissions; admin configures per-audience
      return {
        mothers:      { view: false, add: false, edit: false, delete: false, uploadFiles: false, removeFiles: false, copyContent: false, approveContent: false, publishContent: false },
        fathers:      { view: false, add: false, edit: false, delete: false, uploadFiles: false, removeFiles: false, copyContent: false, approveContent: false, publishContent: false },
        grandparents: { view: false, add: false, edit: false, delete: false, uploadFiles: false, removeFiles: false, copyContent: false, approveContent: false, publishContent: false },
        global:       { viewDashboard: false, manageUsers: false, viewAuditLogs: false },
      };
  }
}
